import React from 'react';

import { FormattedMessage, FormattedNumber, IntlShape } from 'react-intl';

import { dateTimeFormatter, statFormatter } from '@nhl/formatter-utilities';
import { ShortCodeEnums, ReactTableTypes } from '@nhl/nhl-breakaway-ui';
import { CellContext, createColumnHelper } from '@tanstack/react-table';

import CommonMessages from '~/core/commonMessages';
import { PlayerStatAbbrev, PlayerStatNames } from '~/core/enums';
import {
    ASSISTS_COL,
    createSortablePlayerCol,
    GAMES_PLAYED_COL,
    GOALS_COL,
    HEADSHOT_NAME_COL,
    LOSSES_COL,
    POINTS_COL,
    PPG_COL,
    SHG_COL,
    TIES_COL,
    WINS_COL,
} from '~/core/tableColumns/playerTableColumns/columns';
import { FormatPlayerPosition } from '~/core/utils';
import { addSortableHeaderTitle } from '~/views/components/reactTable/utils';

import { GoalieStats, PlayerStats } from './types';

const { playerInfoDescriptions, statsDescriptions } = CommonMessages;

const columnHelper = createColumnHelper<PlayerStats | GoalieStats>();

const PIM_COL = (intl: IntlShape) =>
    createSortablePlayerCol(intl, {
        acc: 'penaltyMinutes',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.penaltyMinutes],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.penaltyMinutes], intl),
    });

const baseStatsColumns = (screenWidth: number, isGoalie: boolean, isWebview: boolean, intl: IntlShape) => [
    columnHelper.display({
        id: 'index',
        header: '',
        cell: ({ rowIndex }: ReactTableTypes.CellContextType<PlayerStats | GoalieStats, unknown>) => rowIndex,
        size: 50,
        meta: {
            headerTitle: intl.formatMessage(CommonMessages.tableHeaders.long.rank),
            style: { textAlign: 'center' },
        },
    }),
    HEADSHOT_NAME_COL(
        intl,
        screenWidth,
        isWebview,
        isGoalie,
        ShortCodeEnums.TeamTriCode[window.__SITE_SETTINGS__.franchise?.triCode?.toUpperCase()]
    ),
];

export const skaterColumns = (screenWidth: number, isGoalie: boolean, isWebview: boolean, intl: IntlShape) => [
    ...baseStatsColumns(screenWidth, isGoalie, isWebview, intl),
    {
        ...createSortablePlayerCol(intl, {
            acc: 'positionCode',
            header: playerInfoDescriptions.abbreviations.position,
            meta: addSortableHeaderTitle(playerInfoDescriptions.long.position, intl),
        }),
        cell: (cell) => <FormatPlayerPosition position={cell?.getValue()} />,
    },
    GAMES_PLAYED_COL(intl),
    GOALS_COL(intl),
    ASSISTS_COL(intl),
    POINTS_COL(intl),
    {
        ...createSortablePlayerCol(intl, {
            acc: 'plusMinus',
            header: statsDescriptions.abbreviations[PlayerStatAbbrev.plusMinus],
            meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.plusMinus], intl),
        }),
        cell: (cell: CellContext<PlayerStats, number> | CellContext<GoalieStats, number>) =>
            cell.getValue() !== undefined ? <FormattedNumber value={cell.getValue()} signDisplay="exceptZero" /> : '',
    },
    PIM_COL(intl),
    PPG_COL(intl),
    SHG_COL(intl),
    createSortablePlayerCol(intl, {
        acc: 'gameWinningGoals',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.gameWinningGoals],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.gameWinningGoals], intl),
    }),
    createSortablePlayerCol(intl, {
        acc: 'overtimeGoals',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.overtimeGoals],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.overtimeGoals], intl),
    }),
    createSortablePlayerCol(intl, { acc: 'shots', header: statsDescriptions.abbreviations[PlayerStatAbbrev.shots] }),
    {
        ...createSortablePlayerCol(intl, {
            acc: 'shootingPctg',
            header: statsDescriptions.abbreviations[PlayerStatAbbrev.shootingPctg],
        }),
        cell: (cell: CellContext<PlayerStats, number>) =>
            statFormatter.displayValueAs('shootingPctg')(cell?.getValue()),
    },
    {
        ...createSortablePlayerCol(intl, {
            acc: 'avgTimeOnIcePerGame',
            header: statsDescriptions.abbreviations[PlayerStatAbbrev.avgTimeOnIce],
        }),
        cell: (cell: CellContext<PlayerStats, number>) =>
            cell?.getValue() === undefined ? (
                <FormattedMessage {...statsDescriptions.noStatIndicator} />
            ) : (
                dateTimeFormatter.displaySecondsFormatted(cell.getValue())
            ),
    },
    {
        ...createSortablePlayerCol(intl, {
            acc: 'avgShiftsPerGame',
            header: statsDescriptions.abbreviations[PlayerStatAbbrev.shiftsPerGame],
        }),
        cell: (cell: CellContext<PlayerStats, number>) =>
            statFormatter.displayValueAs('avgShiftsPerGame')(cell?.getValue()),
    },
    {
        ...createSortablePlayerCol(intl, {
            acc: 'faceoffWinPctg',
            header: statsDescriptions.abbreviations[PlayerStatAbbrev.faceoffPctg],
        }),
        cell: (cell: CellContext<PlayerStats, number>) =>
            statFormatter.displayValueAs('faceoffWinPctg')(cell?.getValue()),
    },
];

export const goalieColumns = (screenWidth: number, isGoalie: boolean, isWebview: boolean, intl: IntlShape) => [
    ...baseStatsColumns(screenWidth, isGoalie, isWebview, intl),
    GAMES_PLAYED_COL(intl),
    createSortablePlayerCol(intl, {
        acc: 'gamesStarted',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.gamesStarted],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.gamesStarted], intl),
    }),
    WINS_COL(intl),
    LOSSES_COL(intl),
    TIES_COL(intl),
    createSortablePlayerCol(intl, {
        acc: 'overtimeLosses',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.overtimeLosses],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.overtimeLosses], intl),
    }),
    {
        ...createSortablePlayerCol(intl, {
            acc: 'goalsAgainstAverage',
            header: statsDescriptions.abbreviations[PlayerStatAbbrev.goalsAgainstAvg],
            meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.goalsAgainstAvg], intl),
        }),
        cell: (cell: CellContext<GoalieStats, number>) =>
            statFormatter.displayValueAs('goalsAgainstAverage')(cell?.getValue(), true),
    },
    {
        ...createSortablePlayerCol(intl, {
            acc: 'savePercentage',
            header: statsDescriptions.abbreviations[PlayerStatAbbrev.savePctg],
            meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.savePctg], intl),
        }),
        cell: (cell: CellContext<GoalieStats, number>) =>
            cell?.getValue() === undefined ? (
                <FormattedMessage {...statsDescriptions.noStatIndicator} />
            ) : (
                statFormatter.displayValueAs('savePercentage')(cell?.getValue())
            ),
    },
    createSortablePlayerCol(intl, {
        acc: 'shotsAgainst',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.shotsAgainst],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.shotsAgainst], intl),
    }),
    createSortablePlayerCol(intl, {
        acc: 'saves',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.saves],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.saves], intl),
    }),
    createSortablePlayerCol(intl, {
        acc: 'goalsAgainst',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.goalsAgainst],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.goalsAgainst], intl),
    }),
    createSortablePlayerCol(intl, {
        acc: 'shutouts',
        header: statsDescriptions.abbreviations[PlayerStatAbbrev.shutouts],
        meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.shutouts], intl),
    }),
    GOALS_COL(intl),
    ASSISTS_COL(intl),
    POINTS_COL(intl),
    PIM_COL(intl),
    {
        ...createSortablePlayerCol(intl, {
            acc: 'timeOnIce',
            header: statsDescriptions.abbreviations[PlayerStatAbbrev.timeOnIce],
            meta: addSortableHeaderTitle(statsDescriptions.long[PlayerStatNames.timeOnIce], intl),
        }),
        cell: (cell: CellContext<GoalieStats, number>) => dateTimeFormatter.displaySecondsFormatted(cell.getValue()),
    },
];
